/*
 * This file is part of the Neos.Neos.Ui package.
 *
 * (c) Contributors of the Neos Project - www.neos.io
 *
 * This package is Open Source Software. For the full copyright and license
 * information, please view the LICENSE file which was distributed with this
 * source code.
 */
import React, {useEffect, useState} from 'react';
// @ts-ignore
import {connect} from 'react-redux';

import {actions, selectors} from '@neos-project/neos-ui-redux-store';
import {GlobalState} from '@neos-project/neos-ui-redux-store/src/System';
import {
    PublishingScope,
    PublishingPhase,
    State as PublishingState,
    PublishingMode
} from '@neos-project/neos-ui-redux-store/src/CR/Publishing';

import {ConfirmationDialog} from './ConfirmationDialog';
import {ProcessIndicator} from './ProcessIndicator';
import {ResultDialog} from './ResultDialog';
import {PartialConflictDialog} from './PartialConflictDialog';

const {
    publishableNodesSelector,
    publishableNodesInDocumentSelector,
    personalWorkspaceNameSelector,
    baseWorkspaceSelector
} = selectors.CR.Workspaces;
const {siteNodeSelector, documentNodeSelector} = selectors.CR.Nodes;

type PublishingDialogProperties =
    | { publishingState: null }
    | {
        publishingState: NonNullable<PublishingState>;
        scopeTitle: string;
        sourceWorkspaceName: string;
        targetWorkspaceName: null | string;
        numberOfChanges: number;
        numberOfSiteChanges: number;
    };

type PublishingDialogHandlers = {
    cancel: () => void;
    confirm: () => void;
    start: (mode: PublishingMode, scope: PublishingScope, requireConfirmation: boolean) => void;
    retry: () => void;
    acknowledge: () => void;
}

type PublishingDialogProps =
    PublishingDialogProperties & PublishingDialogHandlers;
const PublishingDialog: React.FC<PublishingDialogProps> = (props) => {
    const handleCancel = React.useCallback(() => {
        props.cancel();
    }, []);
    const handleConfirm = React.useCallback(() => {
        props.confirm();
    }, []);
    const handleRetry = React.useCallback(() => {
        props.retry();
    }, []);
    const handleAcknowledge = React.useCallback(() => {
        props.acknowledge();
    }, []);
    const handleStart = React.useCallback(() => {
        props.start(PublishingMode.PUBLISH, PublishingScope.SITE, true);
    }, []);

    if (props.publishingState === null) {
        return null;
    }

    switch (props.publishingState.process.phase) {
        case PublishingPhase.START:
            return (
                <ConfirmationDialog
                    mode={props.publishingState.mode}
                    scope={props.publishingState.scope}
                    scopeTitle={props.scopeTitle}
                    sourceWorkspaceName={props.sourceWorkspaceName}
                    targetWorkspaceName={props.targetWorkspaceName}
                    numberOfChanges={props.numberOfChanges}
                    onAbort={handleCancel}
                    onConfirm={handleConfirm}
                    />
            );

        case PublishingPhase.ONGOING: {
            const ongoingScreen = (
                <ProcessIndicator
                    mode={props.publishingState.mode}
                    scope={props.publishingState.scope}
                    scopeTitle={props.scopeTitle}
                    sourceWorkspaceName={props.sourceWorkspaceName}
                    targetWorkspaceName={props.targetWorkspaceName}
                    numberOfChanges={props.numberOfChanges}
                    />
            );

            return props.publishingState.process.autoConfirmed ? <DelayedDisplay component={ongoingScreen} delay={750} /> : ongoingScreen;
        }

        case PublishingPhase.CONFLICTS:
            return null;
        case PublishingPhase.PARTIAL_PUBLISH_CONFLICTS:
            return (
                <PartialConflictDialog
                    mode={props.publishingState.mode}
                    scope={props.publishingState.scope}
                    scopeTitle={props.scopeTitle}
                    sourceWorkspaceName={props.sourceWorkspaceName}
                    targetWorkspaceName={props.targetWorkspaceName}
                    numberOfChanges={props.numberOfChanges}
                    numberOfSiteChanges={props.numberOfSiteChanges}
                    onAbort={handleCancel}
                    onConfirm={handleStart}
                />
            );
        case PublishingPhase.ERROR:
        case PublishingPhase.SUCCESS:
            return (
                <ResultDialog
                    mode={props.publishingState.mode}
                    scope={props.publishingState.scope}
                    scopeTitle={props.scopeTitle}
                    sourceWorkspaceName={props.sourceWorkspaceName}
                    targetWorkspaceName={props.targetWorkspaceName}
                    numberOfChanges={props.numberOfChanges}
                    result={props.publishingState.process}
                    onRetry={handleRetry}
                    onAcknowledge={handleAcknowledge}
                    />
            );
    }
};

const DelayedDisplay = (props: {component: React.ReactElement, delay: number}) => {
    const [enable, setEnable] = useState(false);

    useEffect(() => {
        const id = setTimeout(() => {
            setEnable(true);
        }, props.delay)
        return () => {
            clearTimeout(id)
        }
    }, [props.delay])

    return enable ? props.component : null;
}

export default connect((state: GlobalState): PublishingDialogProperties => {
    const {publishing: publishingState} = state.cr;
    if (publishingState === null) {
        return {publishingState};
    }

    const {scope} = publishingState;
    const sourceWorkspaceName = personalWorkspaceNameSelector(state);
    const baseWorkspace = baseWorkspaceSelector(state);
    const targetWorkspaceName = publishingState.mode === PublishingMode.PUBLISH
        ? baseWorkspace
        : null;

    let numberOfChanges = 0;
    if (publishingState.process.phase === PublishingPhase.SUCCESS) {
        numberOfChanges = publishingState.process.numberOfAffectedChanges;
    } else if (scope === PublishingScope.ALL) {
        numberOfChanges = state.cr.workspaces.personalWorkspace.totalNumberOfChanges;
    } else if (scope === PublishingScope.SITE) {
        numberOfChanges = publishableNodesSelector(state).length;
    } else if (scope === PublishingScope.DOCUMENT) {
        numberOfChanges = publishableNodesInDocumentSelector(state).length;
    }

    const numberOfSiteChanges = publishableNodesSelector(state).length;

    let scopeTitle = 'N/A';
    if (scope === PublishingScope.ALL) {
        scopeTitle = sourceWorkspaceName;
    } else if (scope === PublishingScope.SITE) {
        scopeTitle = siteNodeSelector(state)?.label ?? scopeTitle;
    } else if (scope === PublishingScope.DOCUMENT) {
        scopeTitle = documentNodeSelector(state)?.label ?? scopeTitle;
    }

    return {
        publishingState,
        sourceWorkspaceName,
        targetWorkspaceName,
        numberOfChanges,
        numberOfSiteChanges,
        scopeTitle
    };
}, {
    confirm: (actions as any).CR.Publishing.confirm,
    start: (actions as any).CR.Publishing.start,
    cancel: (actions as any).CR.Publishing.cancel,
    retry: (actions as any).CR.Publishing.retry,
    acknowledge: (actions as any).CR.Publishing.acknowledge
})(PublishingDialog);
